import React from 'react';
import { Box, Button, CardContent, Container, Stack, Typography, useTheme, alpha } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { SOLUTIONS } from '../../data/siteContent';
import { SECTION_SCROLL_MARGIN, scrollToSection } from '../../utils/scroll';
import { MotionBox, MotionCard, fadeInUp, useMotionProps, useReducedMotion } from '../motion';
import SectionHeading from './SectionHeading';

const Solutions = () => {
  const theme = useTheme();
  const motionProps = useMotionProps();
  const reducedMotion = useReducedMotion();

  return (
    <Box
      id="solutions"
      component="section"
      aria-labelledby="solutions-heading"
      sx={{
        scrollMarginTop: SECTION_SCROLL_MARGIN,
        py: { xs: 9, md: 14 },
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'background.default'
      }}
    >
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, background: theme.surfaces.aurora, opacity: 0.7 }} />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: theme.surfaces.grid,
          backgroundSize: theme.surfaces.gridSize,
          maskImage: 'linear-gradient(180deg, #000 0%, transparent 60%)',
          WebkitMaskImage: 'linear-gradient(180deg, #000 0%, transparent 60%)'
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <SectionHeading
          id="solutions-heading"
          eyebrow="Our Products & Services"
          title="Comprehensive Enterprise Solutions"
          subtitle="Built for Sri Lankan businesses, ready for global scale. Our solutions combine international best practices with local market expertise."
        />

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2.5, md: 3.5 },
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'stretch'
          }}
        >
          {SOLUTIONS.map((solution, index) => {
            const Icon = solution.icon;
            return (
              <MotionCard
                key={solution.title}
                {...motionProps(fadeInUp(index * 0.08))}
                whileHover={reducedMotion ? undefined : { y: -6 }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  ...theme.surfaces.glass,
                  borderColor: alpha(solution.color, 0.18),
                  boxShadow: `0 8px 32px ${alpha(solution.color, 0.1)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  // Accent rail; brightens on hover to signal interactivity.
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: solution.gradient,
                    zIndex: 1
                  },
                  '&:hover': {
                    boxShadow: `0 20px 56px ${alpha(solution.color, 0.22)}`,
                    borderColor: alpha(solution.color, 0.35)
                  }
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 4 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Box
                      aria-hidden
                      sx={{
                        width: { xs: 60, md: 72 },
                        height: { xs: 60, md: 72 },
                        flexShrink: 0,
                        borderRadius: 3,
                        background: solution.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        boxShadow: `0 10px 28px ${alpha(solution.color, 0.35)}`
                      }}
                    >
                      <Icon sx={{ fontSize: { xs: 30, md: 36 } }} />
                    </Box>
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 800, lineHeight: 1.3, minWidth: 0 }}>
                      {solution.title}
                    </Typography>
                  </Box>

                  <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                    {solution.description}
                  </Typography>

                  <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 800, mb: 1.5, letterSpacing: 0.3 }}>
                    Key Features
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      listStyle: 'none',
                      p: 0,
                      m: 0,
                      mb: solution.industries.length ? 3 : 0,
                      display: 'grid',
                      gap: 1,
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
                    }}
                  >
                    {solution.features.map((feature) => (
                      <Box component="li" key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle aria-hidden sx={{ fontSize: 16, color: solution.color, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {solution.industries.length > 0 && (
                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 800, mb: 1.5, letterSpacing: 0.3 }}>
                        Industry Applications
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 1,
                          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }
                        }}
                      >
                        {solution.industries.map((industry) => {
                          const IndustryIcon = industry.icon;
                          return (
                            <Box
                              key={industry.label}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                minHeight: 88,
                                backgroundColor: alpha(solution.color, 0.05),
                                border: `1px solid ${alpha(solution.color, 0.12)}`,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center',
                                transition: 'background-color 0.2s ease, transform 0.2s ease',
                                '&:hover': { backgroundColor: alpha(solution.color, 0.1), transform: 'translateY(-2px)' }
                              }}
                            >
                              <Box aria-hidden sx={{ color: solution.color, mb: 0.5, display: 'flex' }}>
                                <IndustryIcon />
                              </Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                {industry.label}
                              </Typography>
                              {industry.count && (
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block', color: solution.color, fontWeight: 700, fontSize: '0.7rem' }}
                                >
                                  {industry.count}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </MotionCard>
            );
          })}
        </Box>

        <MotionBox
          {...motionProps(fadeInUp(0.2))}
          sx={{
            textAlign: 'center',
            mt: { xs: 6, md: 8 },
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            ...theme.surfaces.glass,
            borderColor: alpha(theme.palette.primary.main, 0.16)
          }}
        >
          <Typography variant="h4" component="h3" sx={{ fontWeight: 800, mb: 2 }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography variant="h6" component="p" color="text.secondary" sx={{ mb: 4, maxWidth: 620, mx: 'auto', fontWeight: 400 }}>
            Join 250+ businesses across Sri Lanka who trust SSIPL for their digital transformation journey.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" onClick={() => scrollToSection('contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, borderRadius: 3 }}>
              Schedule a Demo
            </Button>
            <Button variant="outlined" size="large" onClick={() => scrollToSection('clients')} sx={{ px: 4, py: 1.5, fontWeight: 700, borderRadius: 3 }}>
              View Our Clients
            </Button>
          </Stack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Solutions;
